import { computeWordDiff, escHtml } from './wordDiff';

export const inlineMdFormat = (text) => {
  let s = escHtml(text);
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  return s;
};

export const applyMdFormatOnly = (html) => {
  let s = html;
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  return s;
};

export const parseMdLine = (line) => {
  const t = (line || '').trim();
  if (!t) return null;
  const isList = t.startsWith('- ') || t.startsWith('• ');
  const headingMatch = t.match(/^(#{1,6})\s+(.*)/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const tag = `h${Math.min(level + 2, 6)}`;
    return { html: `<${tag}>${inlineMdFormat(headingMatch[2])}</${tag}>`, isList: false, isHeading: true };
  }
  const raw = isList ? t.slice(2) : t;
  return { html: inlineMdFormat(raw), isList };
};

export const diffMdLines = (oldLines, newLines) => {
  const norm = (l) => stripMdPrefix(l.trim());
  const m = oldLines.length, n = newLines.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = norm(oldLines[i - 1]) === norm(newLines[j - 1])
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const ops = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && norm(oldLines[i - 1]) === norm(newLines[j - 1])) {
      ops.push({ type: 'equal', value: newLines[j - 1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.push({ type: 'added', value: newLines[j - 1] }); j--;
    } else {
      ops.push({ type: 'removed', value: oldLines[i - 1] }); i--;
    }
  }
  return ops.reverse();
};

const stripMdPrefix = (line) => line.replace(/^#{1,6}\s+/, '').replace(/^[-•]\s/, '');

export const wordDiffHtml = (oldLine, newLine) => {
  const oldRaw = stripMdPrefix((oldLine || '').trim());
  const newRaw = stripMdPrefix((newLine || '').trim());
  const ops = computeWordDiff(oldRaw, newRaw);
  if (!ops || ops.length === 0) return null;
  let remH = '', addH = '';
  for (const op of ops) {
    const v = escHtml(op.value);
    if (op.type === 'equal') { remH += v; addH += v; }
    else if (op.type === 'removed') { remH += `<span class="wdel">${v}</span>`; }
    else if (op.type === 'added') { addH += `<span class="wins">${v}</span>`; }
  }
  return { rem: applyMdFormatOnly(remH), add: applyMdFormatOnly(addH) };
};

export const groupDiffHunks = (ops) => {
  const hunks = [];
  let cur = null;
  for (const op of ops) {
    if (op.type === 'equal') {
      if (cur) { hunks.push(cur); cur = null; }
      hunks.push({ type: 'equal', lines: [op] });
    } else {
      if (!cur) cur = { type: 'change', lines: [] };
      cur.lines.push(op);
    }
  }
  if (cur) hunks.push(cur);
  return hunks;
};

export function splitCodeImplByRepo(codeImpl, repos) {
  if (!codeImpl) return [];
  const repoNames = (repos || []).map(r => r.name).filter(Boolean);
  if (repoNames.length <= 1) {
    return [{ repoName: repoNames[0] || null, diff: codeImpl.diff, fileContents: codeImpl.fileContents, changedFiles: codeImpl.changedFiles }];
  }

  const dirToRepo = {};
  for (const [fPath] of Object.entries(codeImpl.fileContents || {})) {
    const matched = repoNames.find(name => fPath.startsWith(name + '/'));
    if (matched) {
      const stripped = fPath.substring(matched.length + 1);
      const firstDir = stripped.split('/')[0];
      if (firstDir && !dirToRepo[firstDir]) dirToRepo[firstDir] = matched;
    }
  }

  const inferRepo = (filePath) => {
    const direct = repoNames.find(name => filePath.startsWith(name + '/'));
    if (direct) return direct;
    const firstDir = filePath.split('/')[0];
    return dirToRepo[firstDir] || null;
  };

  const repoBuckets = {};
  for (const name of repoNames) {
    repoBuckets[name] = { diff: [], fileContents: {}, changedFiles: [] };
  }

  if (codeImpl.diff) {
    const sections = codeImpl.diff.split(/^(?=diff --git )/m).filter(s => s.trim());
    for (const section of sections) {
      const bMatch = section.match(/\+\+\+ b\/(.+?)(?:\n|$)/);
      if (!bMatch) continue;
      const filePath = bMatch[1].trim();
      const repo = inferRepo(filePath) || repoNames[0];
      const hasPrefix = filePath.startsWith(repo + '/');
      if (hasPrefix) {
        const stripped = section
          .replace(new RegExp(`a/${repo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`, 'g'), 'a/')
          .replace(new RegExp(`b/${repo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`, 'g'), 'b/');
        repoBuckets[repo].diff.push(stripped);
      } else {
        repoBuckets[repo].diff.push(section);
      }
    }
  }

  for (const [fPath, content] of Object.entries(codeImpl.fileContents || {})) {
    const repo = inferRepo(fPath) || repoNames[0];
    const hasPrefix = fPath.startsWith(repo + '/');
    const stripped = hasPrefix ? fPath.substring(repo.length + 1) : fPath;
    repoBuckets[repo].fileContents[stripped] = content;
  }

  for (const f of (codeImpl.changedFiles || [])) {
    const repo = inferRepo(f) || repoNames[0];
    const hasPrefix = f.startsWith(repo + '/');
    repoBuckets[repo].changedFiles.push(hasPrefix ? f.substring(repo.length + 1) : f);
  }

  return repoNames
    .filter(name => repoBuckets[name].diff.length > 0 || Object.keys(repoBuckets[name].fileContents).length > 0)
    .map(name => ({
      repoName: name,
      diff: repoBuckets[name].diff.join(''),
      fileContents: repoBuckets[name].fileContents,
      changedFiles: repoBuckets[name].changedFiles
    }));
}
